"use client"

import { useTranslations } from "next-intl"
import { useContext, useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { createPortal } from "react-dom"
import { GalleryOpenRefContext } from "@/components/chat/gallery-open-context"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import { CARD_SM, imagePresetUrl } from "@/lib/images/presets"

export type GalleryItem =
  | { id: string; url: string; type: "photo" }
  | { id: string; url: string; type: "listing"; title?: string; price?: string; status?: "cancelled" | "sold" | "gone" | "purchased" | "unavailable" | null }

interface ChatGalleryOverlayProps {
  items: GalleryItem[]
  isOpen: boolean
  onClose: () => void
  onPhotoClick: (url: string, index: number) => void
  onListingClick?: (item: Extract<GalleryItem, { type: "listing" }>) => void
}

export function ChatGalleryOverlay({ items, isOpen, onClose, onPhotoClick, onListingClick }: ChatGalleryOverlayProps) {
  const tChat = useTranslations("chat")
  const [mounted, setMounted] = useState(false)
  const galleryContext = useContext(GalleryOpenRefContext)
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!galleryContext) return
    const { galleryOpenRef: ref, setGalleryOpen } = galleryContext
    if (isOpen) {
      ref.current = true
      setGalleryOpen(true)
      return () => {
        setGalleryOpen(false)
        queueMicrotask(() => { ref.current = false })
      }
    }
    ref.current = false
    setGalleryOpen(false)
  }, [isOpen, galleryContext])

  const label = items.every((a) => a.type === "photo")
    ? `${items.length} Photos`
    : items.every((a) => a.type === "listing")
      ? `${items.length} ${tChat("listing")}`
      : `${items.length} Items`

  if (!mounted) return null

  const portalContainer = galleryContext?.galleryPortalRef?.current ?? document.body

  return createPortal((
    <AnimatePresence>
      {isOpen && (
      <motion.div
        data-image-gallery
        data-chat-gallery-overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-2xl z-[9000] flex flex-col pointer-events-auto"
        onClick={onClose}
      >
        <div className="flex items-center justify-between px-4 py-3 shrink-0" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-[17px] font-semibold text-white">{label}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4 text-white" strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-8 scrollbar-none [&::-webkit-scrollbar]:hidden" onClick={(e) => e.stopPropagation()}>
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-wrap gap-2.5 md:gap-3">
              {items.map((item, idx) => (
                <motion.button
                  key={item.id}
                  type="button"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.04, ease: [0.25, 1, 0.5, 1] }}
                  onClick={() => {
                    if (item.type === "listing" && onListingClick) {
                      onListingClick(item)
                    } else {
                      onPhotoClick(item.url, idx)
                    }
                  }}
                  className={cn(
                    "relative w-[calc(33.333%-7px)] md:w-[calc(25%-10px)] aspect-3/4 rounded-2xl overflow-hidden shadow-lg",
                    "active:scale-95 hover:brightness-90 transition-all cursor-pointer"
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    // A 1/3-width (1/4 on md) 3:4 grid cell — never the full
                    // photo. `CARD_SM` is the aspect-preserving compact-grid
                    // rung, so one URL serves this tile and the `object-cover`
                    // above does the crop. Tapping it opens the real viewer.
                    src={imagePresetUrl(resolveStorageUrl(item.url) || "/placeholder.svg", CARD_SM)}
                    alt={item.type === "listing" ? item.title || "Listing" : "Photo"}
                    className="w-full h-full object-cover"
                  />
                  {item.type === "listing" && item.price && (
                    <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                      <p className="text-[12px] text-white font-bold">{item.price}</p>
                    </div>
                  )}
                  {item.type === "listing" && item.status ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-[11px] font-semibold text-white bg-black/70 px-2.5 py-1 rounded-full">
                        {item.status === "cancelled"
                          ? tChat("cancelled")
                          : item.status === "gone"
                            ? tChat("gone")
                            : item.status === "purchased"
                              ? tChat("purchased")
                              : item.status === "unavailable"
                                ? tChat("unavailable")
                                : tChat("sold")}
                      </span>
                    </div>
                  ) : null}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
      )}
    </AnimatePresence>
  ), portalContainer)
}
